import {
  type ComponentPropsWithRef,
  type MouseEvent,
  type ReactElement,
  useCallback,
  useMemo,
} from 'react';
import type { RowComponentProps } from 'react-window';

import { Checkbox } from '@mui/material';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';

import {
  type IVirtualSelectRowProperties,
  LIST_PADDING,
  VirtualAutocompleteListbox,
} from '@features/autocompleteVirtualization';
import { resolveVirtualRowTopOffset } from '@features/autocompleteVirtualization/utils/resolveVirtualRowTopOffset';

interface IVirtualOptionProperties {
  children?: ReactElement[];
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}

const isVirtualOptionProperties = (
  value: unknown
): value is IVirtualOptionProperties => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.hasOwn(value, 'children') || Object.hasOwn(value, 'onClick');
};

interface IUseVirtualization {
  listWithCheckBox: boolean;
}

type VirtualizedAutocompleteListSlotProperties = ComponentPropsWithRef<'div'>;

export const useVirtualization = ({
  listWithCheckBox,
}: IUseVirtualization): {
  ListComponent: (
    properties: VirtualizedAutocompleteListSlotProperties
  ) => ReactElement;
} => {
  const renderRow = useCallback(
    ({
      index,
      items,
      style,
      ariaAttributes,
    }: RowComponentProps<IVirtualSelectRowProperties>) => {
      const dataSet = items[index];
      const inlineStyle = {
        ...style,
        top: resolveVirtualRowTopOffset(style.top, LIST_PADDING),
      };

      if (Object.hasOwn(dataSet, 'group')) {
        return (
          <ListSubheader
            component="div"
            key={dataSet.key}
            style={inlineStyle}
            {...ariaAttributes}
          >
            {dataSet.group}
          </ListSubheader>
        );
      }

      if (!isVirtualOptionProperties(dataSet.props)) {
        return null;
      }

      const optionProperties = dataSet.props;

      return (
        <Typography
          noWrap
          component="li"
          key={dataSet.key}
          style={inlineStyle}
          {...ariaAttributes}
          {...optionProperties}
          onClick={(event: MouseEvent<HTMLElement>) => {
            optionProperties.onClick?.(event);
          }}
        >
          {listWithCheckBox ? (
            <>
              <Checkbox
                checked={
                  (
                    optionProperties.children?.[0] as ReactElement<{
                      checked?: boolean;
                    }>
                  )?.props?.checked
                }
                style={{ marginRight: '0.5rem' }}
              />
              {optionProperties.children?.[1]}
            </>
          ) : (
            optionProperties.children?.[1]
          )}
        </Typography>
      );
    },
    [listWithCheckBox]
  );

  const ListComponent = useMemo(
    () =>
      function VirtualizedAutocompleteListSlot(
        slotProperties: VirtualizedAutocompleteListSlotProperties
      ) {
        const { ref, ...rest } = slotProperties;

        return (
          <VirtualAutocompleteListbox
            ref={ref}
            virtualRowComponent={renderRow}
            {...rest}
          />
        );
      },
    [renderRow]
  );

  return { ListComponent };
};
